import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSeo } from '../hooks/useSeo.ts';
import { trackEvent } from '../analytics.ts';

/**
 * Paid-ads landing page for the "compliance-intent" keyword cluster
 * (queries like "EAA compliance", "European Accessibility Act compliance", "EAA deadline").
 *
 * Why a dedicated LP and not the home page:
 *  - Headline + sub-headline match the ad copy word-for-word — Google Quality Score
 *    factors landing page relevance ~30% (lower CPC at QS 8-9 vs 5).
 *  - Single conversion path (Pricing CTA), no distractions.
 *  - Tight pricing presence above-the-fold so the visitor doesn't have to scroll-then-search.
 *  - noindex'd so we don't compete with the home page in organic for the same query.
 */
export default function LandingComplianceLP() {
  useSeo({
    title: 'WCAG accessibility audit for EAA-aligned websites — €29',
    canonicalPath: '/lp/eaa-compliance',
    noindex: true,
    // Hreflang chain — declares all language variants of this LP so Google serves the right
    // language in each EU market. The reverse direction (DE/FR → EN) is declared symmetrically
    // in LandingComplianceLpDe.tsx and LandingComplianceLpFr.tsx. Keep all three pages in sync.
    alternates: [
      { lang: 'en',        href: 'https://eaaproof.com/lp/eaa-compliance' },
      { lang: 'de',        href: 'https://eaaproof.com/de/lp/eaa-compliance' },
      { lang: 'fr',        href: 'https://eaaproof.com/fr/lp/eaa-compliance' },
      { lang: 'x-default', href: 'https://eaaproof.com/lp/eaa-compliance' },
    ],
  });

  useEffect(() => {
    trackEvent('lp_view', { lp: 'eaa-compliance' });
  }, []);

  return (
    <div className="page lp-page">
      <section className="lp-hero">
        <span className="hero-badge">
          <span className="hero-badge-dot" aria-hidden="true" />
          European Accessibility Act in force since 28 June 2025
        </span>
        <h1>
          An automated WCAG audit for sites preparing for the <span className="accent">European Accessibility Act</span>.
        </h1>
        <p className="lead">
          The European Accessibility Act (Directive 2019/882) is now in force across EU member
          states. Run an automated WCAG 2.1 AA audit on your site and download a draft
          accessibility statement template — <strong>from €29</strong>, as a starting point for
          your own compliance review.
        </p>

        <div className="lp-cta-row">
          <Link
            to="/pricing"
            className="cta"
            onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance', placement: 'hero' })}
          >
            Run my accessibility audit
          </Link>
          <Link to="/sample" className="cta secondary">See a sample report</Link>
        </div>
        <p className="lp-trust-line">
          <span aria-hidden="true">✓</span> One-time €29
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> Stripe-secure checkout
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> 7-day money-back
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> EU VAT auto-applied
        </p>
      </section>

      <section className="lp-fines">
        <h2>What the European Accessibility Act covers</h2>
        <p className="lp-section-lead">
          The EAA (Directive 2019/882) applies to digital products and services sold to EU
          consumers. Understanding scope is the first step in any compliance review — we
          recommend reviewing the official Directive text and consulting qualified legal
          counsel for advice on how it applies to your specific business.
        </p>
        <div className="lp-fines-grid">
          <article>
            <h3>Who it applies to</h3>
            <p>Businesses selling digital products or services to EU consumers, including e-commerce, banking, e-books, ticketing, and most consumer-facing web apps.</p>
          </article>
          <article>
            <h3>SME exemptions</h3>
            <p>Companies with fewer than 10 employees and under €2M annual turnover are exempt for services (not for physical products) — see Article 4 of the Directive.</p>
          </article>
          <article>
            <h3>Technical standard</h3>
            <p>EN 301 549 is the harmonised standard referenced by the EAA, which currently maps to WCAG 2.1 AA for web content.</p>
          </article>
          <article>
            <h3>Member-state enforcement</h3>
            <p>Each EU member state designates its own enforcement authority and transposes the Directive into national law — penalties and procedures vary by country.</p>
          </article>
        </div>
        <p className="lp-section-disclaimer">
          This information is provided for general awareness only and does not constitute legal
          advice. For binding guidance specific to your situation, please consult a qualified
          legal professional in your jurisdiction.
        </p>
      </section>

      <section className="lp-how">
        <h2>What you get back</h2>
        <ol className="lp-steps">
          <li>
            <strong>Step 1 — Drop your URL.</strong>
            <span> Our scanner loads your site in a real Chromium browser and runs axe-core 4.10 against WCAG 2.1 AA criteria.</span>
          </li>
          <li>
            <strong>Step 2 — Get a detailed PDF audit.</strong>
            <span> Every WCAG violation cited to the relevant EN 301 549 / WCAG 2.1 AA clause, with a plain-English remediation suggestion grounded to your actual HTML.</span>
          </li>
          <li>
            <strong>Step 3 — Use the statement template as a starting point.</strong>
            <span> The generated accessibility statement HTML follows the European Commission's model template (Implementing Decision (EU) 2018/1523). It is a starting draft for your team to review, complete with your organisation's specific details, and have approved by qualified counsel before publication.</span>
          </li>
        </ol>
      </section>

      <section className="lp-pricing">
        <h2>Pricing</h2>
        <div className="lp-pricing-grid">
          <div className="plan-card">
            <h3>Single scan</h3>
            <p className="price">€29<span> one-time</span></p>
            <p className="muted">1 full audit + statement</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance', placement: 'pricing_single' })}
            >Buy single scan</Link>
          </div>
          <div className="plan-card plan-card-featured">
            <p className="plan-badge">Most chosen</p>
            <h3>Basic</h3>
            <p className="price">€9<span>/mo</span></p>
            <p className="muted">5 scans per month</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance', placement: 'pricing_basic' })}
            >Start Basic</Link>
          </div>
          <div className="plan-card">
            <h3>Agency</h3>
            <p className="price">€49<span>/mo</span></p>
            <p className="muted">50 scans, your branding</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance', placement: 'pricing_agency' })}
            >Start Agency</Link>
          </div>
        </div>
      </section>

      <section className="lp-faq">
        <h2>FAQ</h2>
        <details>
          <summary>Is a single automated scan sufficient on its own?</summary>
          <p>
            No automated tool can fully evaluate accessibility on its own. axe-core (the engine
            we use) catches approximately 30–40% of WCAG issues — the deterministic ones like
            missing alt text, contrast failures, and ARIA misuse. Keyboard-only flows,
            screen-reader UX, cognitive load and other criteria still need a qualified human
            reviewer. EAA Proof is a useful starting point and ongoing monitoring tool, not a
            substitute for a manual audit by a certified accessibility professional or for
            legal review.
          </p>
        </details>
        <details>
          <summary>WCAG 2.1 vs 2.2 — which does the report cover?</summary>
          <p>
            EAA references EN 301 549, which currently mandates WCAG 2.1 AA. Our scanner runs the
            full WCAG 2.1 AA ruleset via axe-core. The forthcoming EN 301 549 update will lift the
            baseline to 2.2 AA; we'll ship the upgrade automatically — no plan change needed.
          </p>
        </details>
        <details>
          <summary>What if Claude's remediation suggests a fix that doesn't apply to my code?</summary>
          <p>
            Five grounding guards protect against hallucinated fixes: temperature 0, per-violation
            index echo, strict substring grounding on every code example, and a strip-on-mismatch
            fallback. When a guard fires you still get the axe-core finding — just without the AI
            commentary. Never a fabricated fix.
          </p>
        </details>
        <details>
          <summary>Do I need to host the accessibility statement myself?</summary>
          <p>
            You get a single HTML file you can drop into any static host or CMS at <code>/accessibility</code>.
            We also expose the report PDF + statement via your account dashboard for the lifetime of
            your subscription.
          </p>
        </details>
        <details>
          <summary>Refund policy?</summary>
          <p>
            7-day money-back on the single-scan plan and within the first 14 days of any
            subscription, no questions asked. EU Distance Selling rules also apply.
          </p>
        </details>
      </section>

      <section className="lp-final-cta">
        <h2>Run your WCAG audit</h2>
        <p className="lead">€29. One URL. An automated WCAG 2.1 AA audit and a draft accessibility statement template delivered in your inbox in under 2 minutes — a useful starting point for your own compliance review.</p>
        <Link
          to="/pricing"
          className="cta"
          onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance', placement: 'final' })}
        >Run my WCAG audit</Link>
      </section>
    </div>
  );
}
